import {
  Box,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import JobPictureApi, {
  MissingPicJob,
  Picture,
} from '~/common/api/zeiterfassungServerApi/JobPictureApi';
import {
  Button,
  Heading,
  Image,
  LabeledText,
  Text,
} from '~/common/components/defaultUIComponents';
import DepartmentApi from '~/common/api/zeiterfassungServerApi/DepartmentApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { formatName } from '~/common/utils/employeeUtils';
import * as objectUtils from '~/common/utils/objectUtils';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import useTranslate from '~/common/utils/translate';
import { Paper } from '~/common/components/Paper';
import React from 'react';
import _ from 'lodash';

type NewImageListItemProps = {
  job: MissingPicJob;
};

const useNewImages = (job: MissingPicJob) => {
  const disclosure = useDisclosure();
  const [acceptedPictures, setAcceptedPictures] = React.useState<Picture[]>([]);
  const [unacceptedPictures, setUnacceptedPictures] = React.useState<Picture[]>(
    [],
  );
  const [deletedPictures, setDeletedPictures] = React.useState<Picture[]>([]);

  const initPictures = (pictures: Picture[]) => {
    setAcceptedPictures(_.filter(pictures, objectUtils.is('accepted')));
    setUnacceptedPictures(_.filter(pictures, objectUtils.isNot('accepted')));
  };

  const [{ data: pictures }] = JobPictureApi.hooks.useAllRawJobPictures(
    job.pictures,
    {
      enabled: disclosure.isOpen,
      onSuccess: initPictures,
    },
  );

  const deletePicture = (picture: Picture) => {
    setAcceptedPictures((pictures) => _.without(pictures, picture));
    setUnacceptedPictures((pictures) => _.without(pictures, picture));
    setDeletedPictures((pictures) => [...pictures, picture]);
  };

  const acceptPicture = (picture: Picture) => {
    setUnacceptedPictures((pictures) => _.without(pictures, picture));
    setAcceptedPictures((pictures) => [...pictures, picture]);
  };

  const { mutateAsync: callAccept } =
    JobPictureApi.hooks.useAcceptJobPicture(job);
  const { mutateAsync: callDelete } =
    JobPictureApi.hooks.useRemoveJobPicturesMutation(job);

  const submit = async () => {
    try {
      await callDelete(deletedPictures);
      const difference = _.difference(
        acceptedPictures,
        _.filter(pictures, objectUtils.is('accepted')),
      );
      await Promise.all(difference.map((pic) => callAccept(pic)));
    } catch {
      /* empty */
    }
    disclosure.onClose();
  };

  const onClose = () => {
    initPictures(pictures!);
    disclosure.onClose();
  };

  return {
    acceptPicture,
    acceptedPictures,
    deletePicture,
    deletedPictures,
    unacceptedPictures,
    submit,
    ...disclosure,
    onClose,
  };
};

export const NewImageListItem: React.FC<NewImageListItemProps> = ({ job }) => {
  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const employee = React.useMemo(
    () => employees?.find((e) => parseInt(e.id) === job.employee),
    [employees],
  );
  const translate = useTranslate();
  const [{ data: projects }] = ProjectApi.hooks.useProjectsQuery();
  const project = React.useMemo(
    () => _.find(projects, { id: job.project }),
    [job, projects],
  );
  const [{ data: departments }] = DepartmentApi.hooks.useDepartmentsQuery();
  const department = React.useMemo(
    () => _.find(departments, (d) => d.id === job.department),
    [departments, job],
  );
  const {
    isOpen,
    onOpen,
    onClose,
    acceptedPictures,
    unacceptedPictures,
    submit,
    deletePicture,
    acceptPicture,
  } = useNewImages(job);
  return (
    <>
      <Modal isOpen={isOpen} size="4xl" onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>
            <Heading>dashboard.newpic.add</Heading>
          </ModalHeader>
          <ModalBody>
            <Grid templateColumns="repeat(2, 1fr)" columnGap={6}>
              <GridItem colSpan={2}>
                <SimpleGrid columns={3} columnGap={3}>
                  <LabeledText label="dashboard.newpic.jobname">
                    {job.name}
                  </LabeledText>
                  <LabeledText label="dashboard.newpic.project">
                    {project?.name}
                  </LabeledText>
                  <LabeledText label="dashboard.newpic.department">
                    {department?.name}
                  </LabeledText>
                  <GridItem colSpan={3}>
                    <LabeledText label="dashboard.newjobs.description" col>
                      {job.description}
                    </LabeledText>
                  </GridItem>
                </SimpleGrid>
                <Stack></Stack>
              </GridItem>
              <GridItem overflowY="auto" maxH="60vh">
                <Stack gap={3}>
                  <Heading fontSize="large">dashboard.newpic.accepted</Heading>
                  {acceptedPictures.map((pic) => (
                    <Box key={pic.id} position="relative">
                      <Image src={pic.picture} />
                      <Box
                        position="absolute"
                        top="5"
                        right="5"
                        zIndex={9000}
                        cursor="pointer"
                        onClick={() => deletePicture(pic)}
                      >
                        <DeleteIcon color="red.600" />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </GridItem>
              <GridItem overflowY="auto" maxH="60vh">
                <Stack gap={3}>
                  <Heading fontSize="large">
                    dashboard.newpic.unaccepted
                  </Heading>
                  {unacceptedPictures.map((pic) => (
                    <Box key={pic.id} position="relative">
                      <Image src={pic.picture} />
                      <Box
                        position="absolute"
                        top="5"
                        right="5"
                        zIndex={9000}
                        cursor="pointer"
                        onClick={() => deletePicture(pic)}
                      >
                        <DeleteIcon color="red.600" />
                      </Box>
                      <Box
                        position="absolute"
                        top="5"
                        right="10"
                        zIndex={9000}
                        cursor="pointer"
                        onClick={() => acceptPicture(pic)}
                      >
                        <AddIcon color="green.600" />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </GridItem>
            </Grid>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => void submit()}>
              dashboard.newpic.change
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Paper w="full" h="full" pl={3} py={2}>
        <Grid templateColumns="repeat(2, 2fr)" columnGap={3}>
          <GridItem>
            <LabeledText label="dashboard.newpic.project">
              {project?.name}
            </LabeledText>
          </GridItem>
          <GridItem
            top={0}
            right={0}
            display="flex"
            justifyContent="end"
            gap={3}
          >
            <Text
              color="green.600"
              textDecoration="underline"
              cursor="pointer"
              onClick={onOpen}
            >
              {translate('dashboard.newpic.count', [
                _.filter(
                  job.pictures,
                  objectUtils.isNot('accepted'),
                ).length.toString(),
              ])}
            </Text>
          </GridItem>
          <GridItem>
            <LabeledText label="dashboard.newpic.job">{job.name}</LabeledText>
          </GridItem>
          <GridItem />
          <GridItem>
            <LabeledText label="dashboard.newpic.employee">
              {formatName(employee, translate)}
            </LabeledText>
          </GridItem>
          <GridItem />
          <GridItem>
            <LabeledText label="dashboard.newpic.department">
              {department?.name}
            </LabeledText>
          </GridItem>
        </Grid>
      </Paper>
    </>
  );
};
